import React, { useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Button,
  Menu,
  MenuItem,
  Chip,
  Divider,
  Container,
} from '@mui/material';
import {
  Home as HomeIcon,
  ExpandMore as ExpandMoreIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';

interface CourseNavigationProps {
  currentPage: 'module' | 'exercise' | 'quiz' | 'playground';
}

const CourseNavigation: React.FC<CourseNavigationProps> = ({ currentPage }) => {
  const navigate = useNavigate();
  const { courseId, moduleId } = useParams();
  const [moduleMenuAnchor, setModuleMenuAnchor] = useState<null | HTMLElement>(null);

  const course = useAppSelector(state => (courseId ? state.courses[courseId] : undefined));
  const progress = useAppSelector(state =>
    courseId ? state.progress.courseProgress[courseId] : undefined
  );

  if (!course || !courseId) {
    return null;
  }

  const currentModule = moduleId ? course.modules.find(m => m.id === moduleId) : undefined;
  const currentModuleIndex = currentModule ? course.modules.findIndex(m => m.id === moduleId) : -1;

  const handleModuleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setModuleMenuAnchor(event.currentTarget);
  };

  const handleModuleMenuClose = () => {
    setModuleMenuAnchor(null);
  };

  const handleModuleSelect = (selectedModuleId: string) => {
    navigate(`/courses/${courseId}/modules/${selectedModuleId}`);
    handleModuleMenuClose();
  };

  const handlePlaygroundClick = () => {
    navigate(`/courses/${courseId}/playground`);
  };

  const isModuleCompleted = (modId: string) => {
    return progress?.completedModules.includes(modId) || false;
  };

  const getPageTitle = () => {
    switch (currentPage) {
      case 'module':
        return currentModule ? `${currentModule.title}` : 'Module';
      case 'exercise':
        return 'Exercise';
      case 'quiz':
        return 'Quiz';
      case 'playground':
        return 'Playground';
      default:
        return '';
    }
  };

  return (
    <Box sx={{ bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider', py: 2 }}>
      <Container maxWidth='lg'>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label='breadcrumb' sx={{ mb: 2 }}>
          <Link
            color='inherit'
            href='#'
            onClick={e => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize='inherit' />
            Courses
          </Link>

          <Link
            color='inherit'
            href='#'
            onClick={e => {
              e.preventDefault();
              navigate(`/courses/${courseId}`);
            }}
          >
            {course.title}
          </Link>

          {currentModule && <Typography color='text.primary'>{getPageTitle()}</Typography>}
        </Breadcrumbs>

        {/* Action Bar */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
          }}
        >
          {/* Module Selector */}
          {currentModule && (
            <>
              <Button
                variant='outlined'
                endIcon={<ExpandMoreIcon />}
                onClick={handleModuleMenuOpen}
                sx={{
                  minWidth: { xs: '100%', sm: 200 },
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ textAlign: 'left', overflow: 'hidden' }}>
                  <Typography variant='body2' noWrap>
                    Module {currentModuleIndex + 1}
                  </Typography>
                  <Typography
                    variant='caption'
                    noWrap
                    sx={{ display: { xs: 'block', sm: 'none' } }}
                  >
                    {currentModule.title}
                  </Typography>
                  <Typography variant='body2' noWrap sx={{ display: { xs: 'none', sm: 'block' } }}>
                    {currentModule.title}
                  </Typography>
                </Box>
              </Button>

              <Menu
                anchorEl={moduleMenuAnchor}
                open={Boolean(moduleMenuAnchor)}
                onClose={handleModuleMenuClose}
                PaperProps={{
                  style: {
                    maxHeight: 400,
                    width: 300,
                  },
                }}
              >
                <Typography variant='subtitle2' sx={{ px: 2, py: 1, fontWeight: 'bold' }}>
                  Course Modules
                </Typography>
                <Divider />
                {course.modules.map((module, index) => {
                  const isUnderDevelopment = module.isUnderDevelopment || false;

                  return (
                    <MenuItem
                      key={module.id}
                      onClick={() => !isUnderDevelopment && handleModuleSelect(module.id)}
                      selected={module.id === moduleId}
                      disabled={isUnderDevelopment}
                      sx={{
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        py: 1,
                        opacity: isUnderDevelopment ? 0.6 : 1,
                        cursor: isUnderDevelopment ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 0.5 }}>
                        <Typography
                          variant='body2'
                          sx={{
                            flexGrow: 1,
                            color: isUnderDevelopment ? 'text.disabled' : 'text.primary',
                          }}
                        >
                          Module {index + 1}: {module.title}
                        </Typography>
                        {isUnderDevelopment && (
                          <Chip label='Under Dev' size='small' color='warning' />
                        )}
                        {isModuleCompleted(module.id) && !isUnderDevelopment && (
                          <Chip label='✓' size='small' color='success' />
                        )}
                      </Box>
                      <Typography
                        variant='caption'
                        color={isUnderDevelopment ? 'text.disabled' : 'text.secondary'}
                      >
                        {module.exercises.length} exercises
                        {module.quiz && ' • Quiz'}
                      </Typography>
                    </MenuItem>
                  );
                })}
              </Menu>
            </>
          )}

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              ml: { xs: 0, sm: 'auto' },
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'space-between', sm: 'flex-end' },
            }}
          >
            {/* Playground Button */}
            {course.tools?.playground && (
              <Button
                variant={currentPage === 'playground' ? 'contained' : 'outlined'}
                startIcon={<ScienceIcon />}
                onClick={handlePlaygroundClick}
                size='small'
              >
                Playground
              </Button>
            )}

            {/* Dashboard Button */}
            <Button
              variant='outlined'
              onClick={() => navigate(`/courses/${courseId}`)}
              size='small'
            >
              Dashboard
            </Button>
          </Box>
        </Box>

        {/* Progress Indicator */}
        {progress && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant='caption' color='text.secondary'>
              Progress:
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {course.modules.map(module => {
                const isUnderDevelopment = module.isUnderDevelopment || false;

                return (
                  <Box
                    key={module.id}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: isUnderDevelopment
                        ? 'warning.light'
                        : isModuleCompleted(module.id)
                          ? 'success.main'
                          : module.id === moduleId
                            ? 'primary.main'
                            : 'grey.300',
                      opacity: isUnderDevelopment ? 0.6 : 1,
                    }}
                  />
                );
              })}
            </Box>
            <Typography variant='caption' color='text.secondary'>
              {progress.completedModules.length} of {course.modules.length} modules completed
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default CourseNavigation;
